// ============================================================
// DockerDashboard.jsx
//
// A branded, interactive portal for Docker Mastery.
// Designed with a professional dark glass aesthetic and
// integrated video/image placeholders.
// ============================================================

import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Stack,
    styled,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
    Fab,
    Zoom,
    LinearProgress,
} from "@mui/material";

/* Icons */
import CodeIcon from "@mui/icons-material/Code";
import PersonalVideoIcon from "@mui/icons-material/PersonalVideo";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";

/* Custom Components (Assumed in your project structure) */
import RadioOption from "../../../components/RadioOption";
import Typography from "../../../components/Typography";
import LectureTimer from "../../../components/LectureTimer";
import LanguageTabs from "../../../components/LanguageTabs";

/* Docker Brand Identity */
const DOCKER_BLUE = "#0DB7ED";
const bgImage = "https://images.unsplash.com/photo-1605745341112-85968b193ef5"; // Data center/Ship image

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(0,30,43,0.92), rgba(0,30,43,0.98)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(12),
        paddingBottom: theme.spacing(5),
    },
}));

const DashboardGlassCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(255,255,255,0.03)",
    borderRadius: "16px",
    padding: theme.spacing(3),
    boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
    border: `1px solid rgba(13, 183, 237, 0.15)`,
    height: "100%",
}));

const TopicBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "10px",
    padding: theme.spacing(1.5, 2),
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "rgba(13, 183, 237, 0.1)",
        borderColor: DOCKER_BLUE,
    },
}));

const ViewButton = styled(Button)(({ theme }) => ({
    backgroundColor: DOCKER_BLUE,
    color: "#001E2B",
    fontWeight: 700,
    borderRadius: "999px",
    padding: "6px 20px",
    textTransform: "none",
    "&:hover": {
        backgroundColor: "#0091ca",
        transform: "scale(1.05)",
    },
}));

export default function DockerDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* State Management */
    const [searchQuery, setSearchQuery] = useState("");
    const [showScroll, setShowScroll] = useState(false);
    const [completedCount] = useState(0);

    /* Docker Topics Data */
    const dockerTopics = [
        { title: "Introduction to Containers", link: "/docker-intro" },
        { title: "Docker Architecture", link: "/docker-architecture" },
        { title: "Installation & Setup", link: "/docker-installation" },
        { title: "Essential CLI Commands", link: "/docker-cli" },
        { title: "Docker Images & Layers", link: "/docker-images" },
        { title: "Dockerfile Deep-Dive", link: "/docker-file" },
        { title: "Volumes & Persistence", link: "/docker-volumes" },
        { title: "Docker Networking", link: "/docker-networking" },
        { title: "Docker Compose", link: "/docker-compose" },
        { title: "Environment Management", link: "/docker-environment" },
        { title: "Multi-Stage Builds", link: "/docker-multi-stage" },
        { title: "Security Best Practices", link: "/docker-security" },
        { title: "Docker Hub & Registries", link: "/docker-hub" },
        { title: "Container Orchestration Intro", link: "/docker-orchestration" },
        { title: "Docker in CI/CD", link: "/docker-cicd" },
        { title: "Optimization Techniques", link: "/docker-optimization" },
        { title: "Capstone1: Full-Stack Container", link: "/docker-project" },
        { title: "Capstone2: Full-Stack Container", link: "/docker-project-mysql" },
        { title: "Professional Examination", link: "/docker-examination" },
        { title: "Docker Cheat Sheet", link: "/docker-cheat-sheet" },
    ];

    const filteredTopics = useMemo(() => {
        return dockerTopics.filter(topic =>
            topic.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery]);

    const progressPercentage = (completedCount / dockerTopics.length) * 100;

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <PageWrapper>
            {/* ---------------- DOCKER BANNER ---------------- */}
            <Box sx={{ textAlign: "center", mb: 4, mt: 16, px: 2 }}>
                <Typography
                    variant={isMobile ? "h6" : "h3"}
                    sx={{
                        fontWeight: 900,
                        color: DOCKER_BLUE,
                        px: { xs: 3, md: 6 },
                        py: 1,
                        borderRadius: "999px",
                        display: "inline-block",
                        textShadow: "0 0 15px rgba(13, 183, 237, 0.4)",
                    }}
                >
                    DOCKER & CONTAINERIZATION
                </Typography>
            </Box>

            <LectureTimer />
            <LanguageTabs active="Docker" />

            {/* ---------------- MAIN CONTENT ---------------- */}
            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 }, mt: 2 }}>

                {/* LEFT COLUMN: Overview & Video/Media Section */}
                <Grid item xs={12} md={5}>
                    <DashboardGlassCard>
                        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: "center", mb: 2, color: DOCKER_BLUE }}>
                            LEARNING PORTAL
                        </Typography>

                        <Card sx={{ width: "100%", backgroundColor: "#001E2B", textAlign: "center", padding: "10px", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.1)", mt: 2 }}>
                            {/* VIDEO PLACEHOLDER */}
                            <CardMedia
                                component="video" // Change 'video' to 'img' if you prefer a static thumbnail
                                height={isMobile ? "200" : "260"}
                                image="https://www.docker.com/wp-content/uploads/2022/03/Moby-logo.png"
                                autoPlay
                                loop
                                muted
                                poster="https://www.docker.com/wp-content/uploads/2021/11/docker-container-diagram.png"
                                sx={{ objectFit: "contain", p: 1, bgcolor: "rgba(0,0,0,0.2)", borderRadius: "8px" }}
                            />

                            <CardContent sx={{ color: "white" }}>
                                <Typography variant="subtitle1" fontWeight={800} gutterBottom sx={{ color: DOCKER_BLUE }}>
                                    Enterprise Virtualization
                                </Typography>

                                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                                    <PersonalVideoIcon fontSize="small" />
                                    <Typography variant="body2" fontWeight={600} sx={{ opacity: 0.8 }}>
                                        Ship Any App, Anywhere.
                                    </Typography>
                                </Stack>

                                <Link to="/terminal" style={{ textDecoration: "none" }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<DirectionsBoatIcon />}
                                        sx={{
                                            bgcolor: DOCKER_BLUE,
                                            color: "#001E2B",
                                            fontWeight: "bold",
                                            py: 1.5,
                                            borderRadius: "8px",
                                            "&:hover": { bgcolor: "#0091ca" }
                                        }}
                                    >
                                        Open Docker Terminal
                                    </Button>
                                </Link>
                            </CardContent>
                        </Card>
                    </DashboardGlassCard>
                </Grid>

                {/* RIGHT COLUMN: Curriculum Modules */}
                <Grid item xs={12} md={7}>
                    <DashboardGlassCard>
                        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: "center", mb: 3 }}>
                            CURRICULUM MODULES
                        </Typography>

                        {/* Progress Tracker */}
                        <Box sx={{ mb: 3, px: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: "bold" }}>
                                    Docker Proficiency
                                </Typography>
                                <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: "bold" }}>
                                    {Math.round(progressPercentage)}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progressPercentage}
                                sx={{
                                    height: 8,
                                    borderRadius: 5,
                                    bgcolor: "rgba(255,255,255,0.1)",
                                    "& .MuiLinearProgress-bar": { bgcolor: DOCKER_BLUE }
                                }}
                            />
                        </Box>

                        {/* Search Component */}
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search Docker modules..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ mb: 3 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: DOCKER_BLUE }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    color: "white",
                                    bgcolor: "rgba(255,255,255,0.05)",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(13, 183, 237, 0.3)" }
                                }
                            }}
                        />

                        <Stack spacing={1.5}>
                            {filteredTopics.length > 0 ? (
                                filteredTopics.map((topic, i) => (
                                    <TopicBox key={i}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1 }}>
                                            <RadioOption />
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: "white" }}>
                                                {topic.title}
                                            </Typography>
                                        </Box>
                                        <Link to={topic.link} style={{ textDecoration: "none" }}>
                                            <ViewButton size="small">View</ViewButton>
                                        </Link>
                                    </TopicBox>
                                ))
                            ) : (
                                <Typography sx={{ textAlign: "center", opacity: 0.5, py: 4 }}>
                                    No modules found for "{searchQuery}"
                                </Typography>
                            )}
                        </Stack>
                    </DashboardGlassCard>
                </Grid>
            </Grid>

            {/* Back to Top */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: DOCKER_BLUE, "&:hover": { bgcolor: "#0091ca" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#001E2B" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
